// Вкладка «Посты». Сумма часов в сутки по активным постам — то самое число, из которого
// сервер считает норматив (server/app/domain/norms.py, norm_hours_from_posts): часы всех
// активных постов складываются, сумма умножается на дни месяца.
//
// Складываем ТУ ЖЕ величину и той же арифметикой (десятичные строки, без плавающей точки),
// чтобы человек видел на этой вкладке то же, что на «Нормативе». Норматив при этом всё
// равно приходит с сервера готовым: здесь только слагаемое, а не второй расчёт.
//
// Пост, у которого часов в сутки больше, чем могут отстоять его охранники, подписан
// предупреждением прямо в строке (`post-hours.ts`): его часы уходят в эту сумму и дальше в
// норматив, и заметить опечатку можно только здесь, рядом с суммой.

import { RiAddLine, RiErrorWarningLine, RiShieldUserLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { SiteOut } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours, hoursWord, sumHours } from '@/shared/hours/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { usePostsQuery } from '@/shared/org/queries';
import { лишниеЧасыПоста } from '../post-hours';
import type { SitesRoute } from '../route';
import { FILTER_SITE, openEditPostHref, openNewPostHref, sitesHref } from '../route';

const GUARD_FORMS = ['охранник', 'охранника', 'охранников'] as const;

/** Правка постов — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function PostsTab({ site, route }: { site: SiteOut; route: SitesRoute }) {
  const query = usePostsQuery(site.id);
  const правитьМожно = can(
    useAuthStore((state) => state.permissions),
    EDIT_PERMISSION,
  );

  return (
    <div className='flex flex-col gap-3 p-5'>
      <div className='flex items-center justify-between gap-2'>
        <Link
          to={sitesHref('posts', {
            ...route.search,
            filters: { [FILTER_SITE]: site.id },
            tab: undefined,
          })}
          className='text-paragraph-xs text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
        >
          Показать посты отдельным списком
        </Link>
        {/* На ПУСТОЙ вкладке этой кнопки нет: там же, ниже, стоит такая же в пустом
            состоянии — теми же словами и в то же место. Две одинаковые кнопки на экране
            заставляют искать между ними разницу, которой нет. Когда посты есть, пустого
            состояния не будет, и кнопка здесь единственная. */}
        {(query.data?.items.length ?? 0) > 0 && (
          <Button.Root variant='primary' mode='stroke' size='xsmall' asChild>
            <Link to={openNewPostHref(route, site.id)}>
              <Button.Icon as={RiAddLine} />
              Новый пост
            </Link>
          </Button.Root>
        )}
      </div>

      <ScreenState
        data={query.data}
        error={query.error}
        subject='посты объекта'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-12 w-full' />
            <Skeleton className='h-12 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiShieldUserLine}
            title='Постов у объекта нет'
            description='Пока нет ни одного поста, расчётный норматив часов равен нулю — не «данных нет», а честный ноль по расчёту. Заведите пост, и норматив пересчитается сам.'
            action={
              <Button.Root variant='primary' mode='stroke' size='xsmall' asChild>
                <Link to={openNewPostHref(route, site.id)}>
                  <Button.Icon as={RiAddLine} />
                  Новый пост
                </Link>
              </Button.Root>
            }
          />
        }
      >
        {(data) => {
          const active = data.items.filter((post) => post.is_active);
          const perDay = sumHours(active.map((post) => post.hours_per_day));
          return (
            <div className='flex flex-col gap-3'>
              <h4 className='text-label-sm text-text-strong-950'>
                {countLabel(data.items.length, ['пост', 'поста', 'постов'])}
              </h4>
              {/* Настоящий список, а не столбик блоков: «Править» в строке относится к своему
                  посту, а «Править» в шапке карточки — к объекту, и различает их строка. */}
              <ul className='flex flex-col rounded-xl bg-bg-white-0 ring-1 ring-inset ring-stroke-soft-200'>
                {data.items.map((post) => {
                  const лишние = лишниеЧасыПоста(post.hours_per_day, post.simultaneous_guards);
                  return (
                    <li
                      key={post.id}
                      className='flex flex-col gap-1 border-b border-stroke-soft-200 px-4 py-2.5 last:border-b-0'
                    >
                      <div className='flex items-center gap-3'>
                        <div className='flex min-w-0 flex-1 flex-col'>
                          <span className='truncate text-label-sm text-text-strong-950'>
                            {post.name}
                          </span>
                          <span className='truncate text-paragraph-xs text-text-sub-600'>
                            {countLabel(post.simultaneous_guards, GUARD_FORMS)} одновременно
                            {post.is_active ? '' : ' · пост не действует'}
                          </span>
                        </div>
                        {post.required_guard_category === null ? (
                          <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
                            разряд не задан
                          </span>
                        ) : (
                          <Badge.Root variant='lighter' color='blue' size='medium'>
                            {post.required_guard_category} разряд
                          </Badge.Root>
                        )}
                        <span className='w-24 shrink-0 text-right text-label-sm tabular-nums text-text-strong-950'>
                          {formatHours(post.hours_per_day)} {hoursWord(post.hours_per_day)}
                        </span>
                        {/* Правка стоит В СТРОКЕ, а не только на карточке поста: опечатку в
                            часах человек видит именно здесь, рядом с суммой, из которой
                            считается норматив. Дорога отсюда до карточки поста занимала четыре
                            перехода через отдельный список — столько до починки опечатки никто
                            не идёт. */}
                        {правитьМожно && (
                          <Link
                            to={openEditPostHref(route, site.id, post.id)}
                            className='shrink-0 text-paragraph-xs text-primary-base underline-offset-2 hover:underline тач:inline-flex тач:min-h-11 тач:items-center'
                          >
                            Править
                          </Link>
                        )}
                      </div>
                      {/* Во всю ширину строки, а не третьей строкой под названием: там фраза
                          ломалась бы на четыре строки рядом с разрядом и часами, а правая
                          половина строки съезжала бы к её середине.
                          Оранжевый — только значок. Текст тёмный: оранжевый тон на белом даёт
                          контраст около 2,4 к 1, хуже бледно-серого, который для текста уже
                          запрещён, а это фраза, которую надо прочитать целиком. */}
                      {лишние !== null && (
                        <p className='flex items-start gap-1 text-paragraph-xs text-text-strong-950'>
                          <RiErrorWarningLine
                            className='size-4 shrink-0 text-warning-base'
                            aria-hidden
                          />
                          {лишние}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className='flex items-baseline justify-between gap-3 px-1'>
                <span className='text-paragraph-sm text-text-sub-600'>
                  Сумма часов в сутки по действующим постам
                </span>
                <span className='text-label-md tabular-nums text-text-strong-950'>
                  {formatHours(perDay)} {hoursWord(perDay)}
                </span>
              </div>
              <p className='px-1 text-paragraph-xs text-text-sub-600'>
                Из этой суммы, умноженной на число дней месяца, сервер считает расчётный норматив
                объекта — он на вкладке «Норматив».
              </p>
            </div>
          );
        }}
      </ScreenState>
    </div>
  );
}
